import config from "../config/index";
import fetchService from "../lib/fetchService";
import qs from "qs";

// Persist the OAuth2 token response (identical format to the former /oauth/token
// answer) and load the /me profile. Shared by the job-token login and the
// second MFA step (verify).
const storeTokenAndLoadProfile = tokenResponse => {
  localStorage.setItem("token", tokenResponse["access_token"]);
  localStorage.setItem("refresh", tokenResponse["refresh_token"]);
  const expiresAt = new Date().getTime() + parseInt(tokenResponse["expires_in"]) * 1000;
  localStorage.setItem("expiresAt", expiresAt);

  return fetchService
    .get(
      `${config.urls.BACKEND}/me`,
      {},
      {
        Authorization: `Bearer ${tokenResponse["access_token"]}`,
      }
    )
    .then(res => {
      if (res.status < 200 || res.status >= 300) {
        throw new Error(res.statusText);
      }
      return res.json();
    })
    .then(res => {
      localStorage.setItem("me", JSON.stringify(res));
    });
};

export default {
  /**
   * MFA step 1: exchange username + password for a challengeId. The backend
   * sends a 6-digit code to the user's e-mail. NO token is returned here.
   *
   * This is intentionally NOT part of react-admin's login() flow: a resolved
   * login() would make react-admin redirect away from the login page even
   * though the user is not authenticated yet. The LoginForm calls this
   * directly and only calls login() (below) once the code is entered.
   *
   * @param {{username: string, password: string}} credentials
   * @returns {Promise<string>} the challengeId
   * @throws {Error} error.message holds the HTTP status (e.g. 401, 429)
   */
  initiateMfa: ({ username, password }) => {
    const request = new Request(`${config.urls.BACKEND}/auth/mfa/initiate`, {
      method: "POST",
      credentials: "include",
      mode: "cors",
      cache: "no-cache",
      body: JSON.stringify({ username, password }),
      headers: new Headers({
        "Content-Type": "application/json",
        Accept: "application/json",
        "Accept-Charset": "utf-8",
        "Access-Control-Allow-Origin": "*",
      }),
    });

    return fetch(request)
      .then(response => {
        if (response.status < 200 || response.status >= 300) {
          throw new Error(response.status);
        }
        return response.json();
      })
      .then(response => response["challengeId"]);
  },

  /**
   * Two possible logins:
   *  - Job token login ({ token }): unchanged special path via POST /token.
   *  - MFA step 2 ({ challengeId, code }): verify the e-mail code and, on
   *    success, receive the OAuth2 token (same format as the former
   *    /oauth/token password grant).
   *
   * The former one-step password grant ({ username, password }) is gone —
   * the backend removed it, credentials now go through initiateMfa first.
   */
  login: ({ challengeId, code, token }) => {
    let request;

    if (typeof token === "string") {
      const params = {
        client: config.oauth.CLIENT_ID,
        secret: config.oauth.CLIENT_SECRET,
        token,
      };

      request = new Request(`${config.urls.BACKEND}/token?${qs.stringify(params)}`, {
        method: "POST",
        credentials: "include",
        mode: "cors",
        cache: "no-cache",
        headers: new Headers({
          Accept: "application/json",
          "Accept-Charset": "utf-8",
          "Access-Control-Allow-Origin": "*",
        }),
      });
    } else {
      request = new Request(`${config.urls.BACKEND}/auth/mfa/verify`, {
        method: "POST",
        credentials: "include",
        mode: "cors",
        cache: "no-cache",
        body: JSON.stringify({ challengeId, code }),
        headers: new Headers({
          "Content-Type": "application/json",
          Accept: "application/json",
          "Accept-Charset": "utf-8",
          "Access-Control-Allow-Origin": "*",
        }),
      });
    }

    return fetch(request)
      .then(response => {
        if (response.status < 200 || response.status >= 300) {
          throw new Error(response.status);
        }
        return response.json();
      })
      .then(storeTokenAndLoadProfile);
  },

  logout: ignoredParams => {
    localStorage.removeItem("token");
    localStorage.removeItem("refresh");
    localStorage.removeItem("expiresAt");
    localStorage.removeItem("me");
    return Promise.resolve();
  },

  checkAuth: ignoredParams => {
    const token = localStorage.getItem("token");
    if (!token) return Promise.reject();

    const expiresAt = localStorage.getItem("expiresAt");
    if (!expiresAt || new Date().getTime() > parseInt(expiresAt)) {
      return Promise.reject();
    }

    return Promise.resolve();
  },

  checkError: error => {
    const status = error.status;
    if (status === 401 || status === 403) {
      localStorage.removeItem("token");
      localStorage.removeItem("refresh");
      localStorage.removeItem("expiresAt");
      localStorage.removeItem("me");
      return Promise.reject();
    }
    return Promise.resolve();
  },

  getPermissions: () => {
    const me = localStorage.getItem("me");
    return me ? Promise.resolve(JSON.parse(me)) : Promise.reject();
  },
};
